/**
 * Architectural floorplan fixture symbols.
 *
 * Each symbol is a list of SVG primitives (path | rect | circle) within its
 * native viewBox. The same data renders in:
 *   - React preview:  <svg viewBox={sym.viewBox}>{sym.elements.map(...)}</svg>
 *   - Canvas burn-in: per-element Path2D construction, transformed from
 *                     symbol viewBox space to fixture box pixel space.
 *
 * Caller controls stroke/fill — primitives are pure geometry.
 *
 * viewBox is per-symbol: external SVGs (toilet, sink, bathtub) use their
 * native artistic proportions (e.g. 86×125, 231×106). Custom symbols use 100×100.
 * preserveAspectRatio behavior is decided by the renderer (xMidYMid meet
 * preserves shape with letterboxing; 'none' stretches to fill the user box).
 */

export type FixtureSymbolKey =
  | 'toilet'
  | 'shower'
  | 'bathtub'
  | 'sink'
  | 'sink_double'
  | 'kitchen_counter'
  | 'kitchen_island';

export type FixtureSymbolElement =
  | { type: 'path'; d: string }
  | { type: 'rect'; x: number; y: number; width: number; height: number; rx?: number; ry?: number }
  | { type: 'circle'; cx: number; cy: number; r: number };

export interface FixtureSymbol {
  label: string;
  viewBox: string;
  elements: FixtureSymbolElement[];
}

export const FIXTURE_SYMBOLS: Record<FixtureSymbolKey, FixtureSymbol> = {
  // External: toilet-1.svg — bowl outer, tank, inner seat, flush button
  toilet: {
    label: 'Toilette',
    viewBox: '0 0 86 125',
    elements: [
      { type: 'path', d: 'M4.251 68.276c0-30.773 16.825-55.568 37.857-55.568 20.81 0 37.857 24.795 37.857 55.568s-17.047 55.568-37.857 55.568c-21.032 0-37.857-24.795-37.857-55.568z' },
      { type: 'path', d: 'M0 0v9.962s1.992 11.955 42.064 11.955S84.127 9.962 84.127 9.962L83.906 0H0z' },
      { type: 'path', d: 'M17.246 73.722c0-20.146 11.069-36.529 24.795-36.529s24.795 16.383 24.795 36.529-11.069 36.529-24.795 36.529-24.795-16.383-24.795-36.529z' },
      { type: 'path', d: 'M37.237 10.87c0-2.435 2.214-4.428 4.871-4.428s4.871 1.993 4.871 4.428-2.214 4.428-4.871 4.428-4.871-1.993-4.871-4.428z' },
    ],
  },

  // Custom: square + diagonal X + bullseye drain (concentric rings)
  shower: {
    label: 'Dusche',
    viewBox: '0 0 100 100',
    elements: [
      {
        type: 'path',
        d:
          'M 8 8 H 92 V 92 H 8 Z ' +
          'M 8 8 L 92 92 ' +
          'M 92 8 L 8 92 ' +
          'M 50 50 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0 ' +
          'M 50 50 m -3 0 a 3 3 0 1 0 6 0 a 3 3 0 1 0 -6 0',
      },
    ],
  },

  // Simplified outer+inner rounded rect + drain + spout (derived from bath-tub.svg
  // geometry, dropped headrest pill, water polygon, and 3 faucet buttons)
  bathtub: {
    label: 'Badewanne',
    viewBox: '0 0 231 106',
    elements: [
      { type: 'path', d: 'M41.045 0h147c22.591 0 41.046 23.546 41.046 52.5S210.636 105 188.045 105h-147C18.455 105 0 81.455 0 52.5S18.455 0 41.045 0zm13.364 14.477h126.636c19.409 0 35.318 17.659 35.318 39.614s-15.909 39.614-35.318 39.614H54.409c-19.409 0-35.318-17.659-35.318-39.614S35 14.477 54.409 14.477z' },
      { type: 'circle', cx: 178.5, cy: 52.5, r: 5 },
      { type: 'path', d: 'M148.782 6.491h1.909c.955 0 1.591.636 1.591 1.273v11.773c0 .636-.636 1.273-1.591 1.273h-1.909c-.955 0-1.591-.636-1.591-1.273V7.764c0-.636.636-1.273 1.591-1.273z' },
    ],
  },

  // External: sink-1.svg — counter, basin, faucet base, two tap pipes
  sink: {
    label: 'Waschbecken',
    viewBox: '0 0 80 95',
    elements: [
      { type: 'path', d: 'M6.642 0H71.73c3.542 0 6.42 2.435 6.42 5.313v83.242c0 2.878-2.878 5.313-6.42 5.313H6.642C2.878 93.868 0 91.433 0 88.555V5.313C0 2.435 2.878 0 6.642 0z' },
      { type: 'path', d: 'M25.748 4.273h43.613c2.435 0 4.428 2.214 4.428 4.871v75.714c0 2.435-1.993 4.649-4.428 4.649H25.748c-2.435 0-4.428-2.214-4.428-4.649V9.144c0-2.657 1.993-4.871 4.428-4.871z' },
      { type: 'path', d: 'M8.014 43.945h22.139v5.977H8.014v-5.977z' },
      { type: 'path', d: 'M12.796 30.574v11.512' },
      { type: 'path', d: 'M12.619 51.406V63.14' },
    ],
  },

  // External: double-sink.svg — outer counter, two basins, two faucet bases, four taps
  sink_double: {
    label: 'Doppel-Waschbecken',
    viewBox: '0 0 72 155',
    elements: [
      { type: 'rect', x: 0, y: 0, width: 70.5, height: 153.5, rx: 5 },
      { type: 'rect', x: 14, y: 4, width: 51.5, height: 67.5, rx: 5 },
      { type: 'rect', x: 14, y: 82, width: 51.5, height: 67.5, rx: 5 },
      { type: 'path', d: 'M5.5 34.5H29v5H5.5z' },
      { type: 'circle', cx: 9.5, cy: 29, r: 2.5 },
      { type: 'circle', cx: 9.5, cy: 45, r: 2.5 },
      { type: 'circle', cx: 9.5, cy: 121.5, r: 2.5 },
      { type: 'path', d: 'M5.5 111H29v5H5.5z' },
      { type: 'circle', cx: 9.5, cy: 105.5, r: 2.5 },
    ],
  },

  // Custom: long rect + 2 stove burners + sink basin
  kitchen_counter: {
    label: 'Küchenzeile',
    viewBox: '0 0 100 100',
    elements: [
      {
        type: 'path',
        d:
          'M 5 30 H 95 V 70 H 5 Z ' +
          'M 22 50 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0 ' +
          'M 45 50 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0 ' +
          'M 65 38 H 90 V 62 H 65 Z',
      },
    ],
  },

  // Custom: outer rect + inner outline (double border)
  kitchen_island: {
    label: 'Küchen-Insel',
    viewBox: '0 0 100 100',
    elements: [
      {
        type: 'path',
        d:
          'M 8 25 H 92 V 75 H 8 Z ' +
          'M 14 31 H 86 V 69 H 14 Z',
      },
    ],
  },
};
